import {
  KirbyClauseDto,
  KirbyFilterDraft,
  KirbyFilterDto,
  KirbyFilterGroupNode,
  KirbyFilterNode,
  KirbyFilterOption,
  KirbyFilterPropertyDefinition,
  KirbyFilterRequest,
  KirbyFilterValidationIssue,
} from '../types/kirbyFiltering';

export const maximumFilterClauseCount = 25;
export const maximumFilterGroupDepth = 5;
export const maximumFilterValueLength = 500;

const readString = (value: unknown, keys: string[]): string | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;

  const record = value as Record<string, unknown>;
  const matchedKey = Object.keys(record).find((key) =>
    keys.some((candidate) => candidate.toLowerCase() === key.toLowerCase()),
  );
  const matchedValue = matchedKey ? record[matchedKey] : undefined;
  return typeof matchedValue === 'string' && matchedValue.trim() ? matchedValue.trim() : undefined;
};

const normalizeOption = (value: unknown): KirbyFilterOption | null => {
  if (typeof value === 'string' && value.trim()) {
    return { label: value.trim(), value: value.trim() };
  }

  const optionValue = readString(value, ['value']);
  if (!optionValue) return null;

  return { label: readString(value, ['label']) ?? optionValue, value: optionValue };
};

/** Normalizes and deduplicates the filter categories returned by Kirby. */
export const normalizeAvailableFilterTypes = (response: unknown): string[] => {
  if (!Array.isArray(response)) return [];

  return [
    ...new Set(response.filter((value): value is string => typeof value === 'string').map((value) => value.trim())),
  ].filter(Boolean);
};

/** Normalizes both observed Kirby property-definition response shapes. */
export const normalizeFilterPropertyDefinitions = (response: unknown): KirbyFilterPropertyDefinition[] => {
  if (!Array.isArray(response)) return [];

  return response.flatMap((value) => {
    if (typeof value !== 'object' || value === null) return [];

    const record = value as Record<string, unknown>;
    const property = normalizeOption(record.property ?? record.Property);
    const rawOperators = record.availableOperators ?? record.AvailableOperators;
    if (!property || !Array.isArray(rawOperators)) return [];

    const availableOperators = rawOperators.flatMap((operator) => {
      const normalizedOperator = normalizeOption(operator);
      return normalizedOperator ? [normalizedOperator] : [];
    });

    return [
      {
        property,
        availableOperators,
        propertyInputType: readString(record, ['propertyInputType']) ?? 'Unknown',
        allowNullInput: record.allowNullInput === true || record.AllowNullInput === true,
        hasValueListAvailable: record.hasValueListAvailable === true || record.HasValueListAvailable === true,
      },
    ];
  });
};

/** Validates a draft against the currently loaded property metadata. */
export const validateFilterDraft = (
  draft: KirbyFilterDraft,
  definitions: KirbyFilterPropertyDefinition[],
): KirbyFilterValidationIssue[] => {
  const issues: KirbyFilterValidationIssue[] = [];
  let clauseCount = 0;
  if (!draft.filterType) issues.push({ field: 'filterType', message: 'Select a filter category.' });

  const validateNode = (node: KirbyFilterNode, depth: number) => {
    if (node.kind === 'group') {
      if (depth > maximumFilterGroupDepth) {
        issues.push({
          nodeId: node.id,
          field: 'filter',
          message: `Nested groups cannot exceed ${maximumFilterGroupDepth} levels.`,
        });
      }
      if (node.children.length === 0) {
        issues.push({ nodeId: node.id, field: 'filter', message: 'Add at least one clause or group.' });
      }
      node.children.forEach((child) => validateNode(child, depth + 1));
      return;
    }

    const clause = node;
    clauseCount += 1;
    if (clauseCount === maximumFilterClauseCount + 1) {
      issues.push({
        nodeId: draft.root.id,
        field: 'filter',
        message: `A query can contain at most ${maximumFilterClauseCount} clauses.`,
      });
    }
    const definition = definitions.find((candidate) => candidate.property.value === clause.property);
    if (!clause.property || !definition) {
      issues.push({ nodeId: clause.id, field: 'property', message: 'Select an available property.' });
      return;
    }

    if (!definition.availableOperators.some((operator) => operator.value === clause.operator)) {
      issues.push({ nodeId: clause.id, field: 'operator', message: 'Select an available operator.' });
    }

    if (clause.operator === 'IsNull' || clause.operator === 'IsNotNull') {
      issues.push({
        nodeId: clause.id,
        field: 'operator',
        message: 'Null-check filters are not available until the Kirby payload contract is confirmed.',
      });
    }

    if (!clause.value.trim()) {
      issues.push({ nodeId: clause.id, field: 'value', message: 'Enter a value.' });
    }

    if (clause.value.length > maximumFilterValueLength) {
      issues.push({
        nodeId: clause.id,
        field: 'value',
        message: `Filter values cannot exceed ${maximumFilterValueLength} characters.`,
      });
    }

    if (definition.propertyInputType === 'Number' && clause.value.trim() && !Number.isFinite(Number(clause.value))) {
      issues.push({ nodeId: clause.id, field: 'value', message: 'Enter a valid number.' });
    }

    if (!['Date', 'Number', 'Text', 'Other'].includes(definition.propertyInputType)) {
      issues.push({
        nodeId: clause.id,
        field: 'value',
        message: `${definition.propertyInputType} values are not available in this first pass.`,
      });
    }
  };

  validateNode(draft.root, 0);

  return issues;
};

const serializeClause = (clause: Extract<KirbyFilterNode, { kind: 'clause' }>): KirbyClauseDto => ({
  Clause: {
    Property: clause.property,
    Operator: clause.operator,
    Value: clause.value,
  },
});

const serializeNode = (node: KirbyFilterNode): KirbyFilterDto => {
  if (node.kind === 'clause') return serializeClause(node);

  return {
    Conjunction: node.conjunction,
    Children: node.children.map(serializeNode),
  };
};

/** Replaces one node in a Kirby filter tree without mutating the current draft. */
export const replaceFilterNode = (
  root: KirbyFilterGroupNode,
  nodeId: string,
  replacement: KirbyFilterNode,
): KirbyFilterGroupNode => {
  if (root.id === nodeId) return replacement.kind === 'group' ? replacement : root;

  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === nodeId) return replacement;
      return child.kind === 'group' ? replaceFilterNode(child, nodeId, replacement) : child;
    }),
  };
};

/** Updates one node in a Kirby filter tree using its latest state. */
export const updateFilterNode = (
  root: KirbyFilterGroupNode,
  nodeId: string,
  update: (node: KirbyFilterNode) => KirbyFilterNode,
): KirbyFilterGroupNode => {
  if (root.id === nodeId) {
    const updatedRoot = update(root);
    return updatedRoot.kind === 'group' ? updatedRoot : root;
  }

  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === nodeId) return update(child);
      return child.kind === 'group' ? updateFilterNode(child, nodeId, update) : child;
    }),
  };
};

/** Removes one non-root node from a Kirby filter tree without mutating the current draft. */
export const removeFilterNode = (root: KirbyFilterGroupNode, nodeId: string): KirbyFilterGroupNode => ({
  ...root,
  children: root.children
    .filter((child) => child.id !== nodeId)
    .map((child) => (child.kind === 'group' ? removeFilterNode(child, nodeId) : child)),
});

/** Serializes a validated recursive draft into Kirby's clause/group contract. */
export const serializeFilterDraft = (draft: KirbyFilterDraft, pageNumber = 1, pageSize = 100): KirbyFilterRequest => {
  if (draft.root.children.length === 0) return { Filter: null, PageNumber: pageNumber, PageSize: pageSize };
  if (draft.root.children.length === 1 && draft.root.children[0].kind === 'clause') {
    return { Filter: serializeClause(draft.root.children[0]), PageNumber: pageNumber, PageSize: pageSize };
  }

  return { Filter: serializeNode(draft.root), PageNumber: pageNumber, PageSize: pageSize };
};
